import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import noop from 'lodash/noop'
import { Button } from '@/components/button'
import { DeleteIcon } from '@/components/icons'
import { colors, breakpoints } from '@/styles'
import SmartButton from './button'
import LoadingText from './loading'

interface GeneratedTextAreaProps {
  active?: boolean
  text?: string
  isLoading?: boolean
  getUpdatedDescription: (type: string, content: string) => void
  handleUseClick?: (val: string) => void
  handleRemove?: () => void
}

const Wrapper = styled.div``

export const TextAreaContainer = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 16px;
  background-color: ${props => (props.active ? colors.grey96 : colors.white)};
  border-radius: 12px;
`

export const FuaxTextArea = styled.div<{ active?: boolean }>`
  position: relative;
  background: ${colors.white};
  border: 1px solid ${colors.grey80};
  box-sizing: border-box;
  padding: 16px 16px 64px 16px;
  border-radius: 8px;
  white-space: pre-wrap;

  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.black};
`

const ButtonWrapper = styled.div`
  white-space: pre-wrap;

  > button {
    margin-top: 16px;
    margin-right: 12px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 12px;
    column-gap: 12px;
    margin-top: 12px;

    > button {
      margin-top: 0px;
      margin-right: 0px;
    }
  }
`

const ButtonBarWrapper = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  right: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 10px 10px 10px;
  background: ${colors.white};
  box-sizing: border-box;

  border-top: none;
  border-radius: 0 0 6px 6px;
`

const GeneratedTextArea: React.FC<GeneratedTextAreaProps> = ({
  active = false,
  text = '',
  isLoading = false,
  getUpdatedDescription,
  handleUseClick = noop,
  handleRemove
}) => {
  const { t } = useTranslation()

  const AI_OPTIONS = [
    {
      id: 'application-description-ai-shorter',
      key: 'shorter',
      title: t('l.smart_description.generate_text.shorter.label', 'Shorten it')
    },
    {
      id: 'application-description-ai-simpler',
      key: 'simpler',
      title: t('l.smart_description.generate_text.simpler.label', 'Simplify it')
    },
    {
      id: 'application-description-ai-descriptive',
      key: 'descriptive',
      title: t('l.smart_description.generate_text.descriptive.label', 'More descriptive"')
    },
    {
      id: 'application-description-ai-professional',
      key: 'professional',
      title: t('l.smart_description.generate_text.professional.label', 'Professional tone')
    },
    {
      id: 'application-description-ai-friendly',
      key: 'friendly',
      title: t('l.smart_description.generate_text.friendly.label', 'Friendly tone')
    },
    {
      id: 'application-description-ai-casual',
      key: 'casual',
      title: t('l.smart_description.generate_text.casual.label', 'Casual tone')
    }
  ]

  return (
    <Wrapper>
      <TextAreaContainer active={active}>
        <FuaxTextArea>
          <>
            {isLoading && <LoadingText />}
            <span>{text}</span>
            {!isLoading && (
              <ButtonBarWrapper>
                <Button
                  id="application-description-ai-usethis-click"
                  theme={active ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => handleUseClick(text)}
                >
                  {t('l.smart_description.generate_text.use_this.label', 'Use this')}
                </Button>
                {active && !isLoading && (
                  <Button theme="delete" size="small" onClick={handleRemove}>
                    <DeleteIcon />
                  </Button>
                )}
              </ButtonBarWrapper>
            )}
          </>
        </FuaxTextArea>
        {active && !isLoading && (
          <ButtonWrapper>
            {AI_OPTIONS.map(option => (
              <SmartButton
                key={option?.key}
                onClick={() => getUpdatedDescription(option?.key, text)}
              >
                {option?.title}
              </SmartButton>
            ))}
          </ButtonWrapper>
        )}
      </TextAreaContainer>
    </Wrapper>
  )
}

export default GeneratedTextArea
